import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Qualitaetspruefung, Zertifikat, Reklamation } from '../types';
import './QualitaetOverview.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

type TabType = 'pruefungen' | 'zertifikate' | 'reklamationen';

const QualitaetOverview: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('pruefungen');
  const [pruefungen, setPruefungen] = useState<Qualitaetspruefung[]>([]);
  const [zertifikate, setZertifikate] = useState<Zertifikat[]>([]);
  const [reklamationen, setReklamationen] = useState<Reklamation[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = async (tab: TabType) => {
    setLoading(true);
    try {
      if (tab === 'pruefungen') {
        const res = await axios.get(`${API_URL}/api/pruefungen`);
        setPruefungen(res.data);
      } else if (tab === 'zertifikate') {
        const res = await axios.get(`${API_URL}/api/zertifikate`);
        setZertifikate(res.data);
      } else {
        const res = await axios.get(`${API_URL}/api/reklamationen`);
        setReklamationen(res.data);
      }
    } catch (error) {
      console.error('Fehler beim Laden:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(activeTab);
  }, [activeTab]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE');
  };

  const getErgebnisClass = (ergebnis: string) => {
    const map: Record<string, string> = {
      'Bestanden': 'ergebnis-bestanden',
      'Nacharbeit': 'ergebnis-nacharbeit',
      'Gesperrt': 'ergebnis-gesperrt',
    };
    return map[ergebnis] || '';
  };

  const getZertStatusClass = (status: string, gueltig_bis?: string) => {
    if (gueltig_bis) {
      const tage = Math.floor((new Date(gueltig_bis).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      if (tage <= 30 && tage > 0 && status === 'Gültig') return 'zert-bald-ablaufend';
    }
    const map: Record<string, string> = {
      'Gültig': 'zert-gueltig',
      'Abgelaufen': 'zert-abgelaufen',
      'Widerrufen': 'zert-widerrufen',
    };
    return map[status] || '';
  };

  const getZertTypClass = (typ: string) => {
    const map: Record<string, string> = {
      'FSC': 'zert-typ-fsc',
      'PEFC': 'zert-typ-pefc',
      'EUDR': 'zert-typ-eudr',
    };
    return map[typ] || '';
  };

  const getReklStatusClass = (status: string) => {
    const map: Record<string, string> = {
      'Offen': 'rekl-offen',
      'In Bearbeitung': 'rekl-in_bearbeitung',
      'Gelöst': 'rekl-geloest',
      'Abgelehnt': 'rekl-abgelehnt',
    };
    return map[status] || '';
  };

  const renderPruefungen = () => (
    <div className="table-container">
      <table className="qual-table">
        <thead>
          <tr>
            <th>Charge</th>
            <th>Artikel</th>
            <th>Datum</th>
            <th>Prüfer</th>
            <th>Bewertung</th>
            <th>Messwerte</th>
            <th>Ergebnis</th>
            <th>Notizen</th>
          </tr>
        </thead>
        <tbody>
          {pruefungen.map((p) => (
            <tr key={p.pruefung_id}>
              <td style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--green-800)' }}>{p.charge_nr}</td>
              <td>{p.artikel_bezeichnung || '-'}</td>
              <td>{formatDate(p.pruef_datum)}</td>
              <td>{p.pruefer || '-'}</td>
              <td>{p.bewertung || '-'}</td>
              <td style={{ fontSize: 12 }}>
                {p.custom_attributes && Object.keys(p.custom_attributes).length > 0
                  ? Object.entries(p.custom_attributes).map(([k, v]) => `${k}: ${v}`).join(', ')
                  : '-'}
              </td>
              <td>
                <span className={`status-badge ${getErgebnisClass(p.ergebnis)}`}>
                  {p.ergebnis}
                </span>
              </td>
              <td className="qual-notizen" title={p.notizen || ''}>{p.notizen || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderZertifikate = () => (
    <div className="table-container">
      <table className="qual-table">
        <thead>
          <tr>
            <th>Typ</th>
            <th>Nummer</th>
            <th>Aussteller</th>
            <th>Partner</th>
            <th>Gültig von</th>
            <th>Gültig bis</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {zertifikate.map((z) => (
            <tr key={z.zertifikat_id}>
              <td>
                <span className={`zert-typ ${getZertTypClass(z.typ)}`}>{z.typ}</span>
              </td>
              <td style={{ fontFamily: 'monospace' }}>{z.nummer}</td>
              <td>{z.aussteller || '-'}</td>
              <td>{z.partner_name || '-'}</td>
              <td>{formatDate(z.gueltig_von)}</td>
              <td>{formatDate(z.gueltig_bis)}</td>
              <td>
                <span className={`status-badge ${getZertStatusClass(z.status || '', z.gueltig_bis)}`}>
                  {z.status}
                  {z.gueltig_bis && (() => {
                    const tage = Math.floor((new Date(z.gueltig_bis).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                    if (tage <= 30 && tage > 0 && z.status === 'Gültig') return ` (${tage}d)`;
                    return '';
                  })()}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderReklamationen = () => (
    <div className="table-container">
      <table className="qual-table">
        <thead>
          <tr>
            <th>Nr.</th>
            <th>Kunde</th>
            <th>Artikel</th>
            <th>Datum</th>
            <th>Beschreibung</th>
            <th>Status</th>
            <th>Kosten</th>
          </tr>
        </thead>
        <tbody>
          {reklamationen.map((r) => (
            <tr key={r.reklamation_id}>
              <td style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--green-800)' }}>{r.reklamation_nr}</td>
              <td>{r.kunde_name || '-'}</td>
              <td>{r.artikel_bezeichnung || '-'}</td>
              <td>{formatDate(r.datum)}</td>
              <td className="qual-notizen" title={r.beschreibung}>{r.beschreibung}</td>
              <td>
                <span className={`status-badge ${getReklStatusClass(r.status || '')}`}>
                  {r.status}
                </span>
              </td>
              <td className={r.kosten ? 'qual-kosten' : ''}>
                {r.kosten ? `${r.kosten.toFixed(2)} €` : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const getEmptyMessage = () => {
    const messages: Record<TabType, string> = {
      pruefungen: 'Keine Qualitätsprüfungen vorhanden',
      zertifikate: 'Keine Zertifikate vorhanden',
      reklamationen: 'Keine Reklamationen vorhanden',
    };
    return messages[activeTab];
  };

  const getData = () => {
    if (activeTab === 'pruefungen') return pruefungen;
    if (activeTab === 'zertifikate') return zertifikate;
    return reklamationen;
  };

  return (
    <div className="qual-container">
      <div className="list-header">
        <h2>Qualitätsmanagement</h2>
      </div>

      <div className="qual-tabs">
        <button
          className={`qual-tab ${activeTab === 'pruefungen' ? 'active' : ''}`}
          onClick={() => setActiveTab('pruefungen')}
        >
          Prüfungen ({pruefungen.length})
        </button>
        <button
          className={`qual-tab ${activeTab === 'zertifikate' ? 'active' : ''}`}
          onClick={() => setActiveTab('zertifikate')}
        >
          Zertifikate ({zertifikate.length})
        </button>
        <button
          className={`qual-tab ${activeTab === 'reklamationen' ? 'active' : ''}`}
          onClick={() => setActiveTab('reklamationen')}
        >
          Reklamationen ({reklamationen.length})
        </button>
      </div>

      {loading ? (
        <div className="loading">Lädt...</div>
      ) : getData().length === 0 ? (
        <div className="empty-state">
          <p>{getEmptyMessage()}</p>
        </div>
      ) : (
        <>
          {activeTab === 'pruefungen' && renderPruefungen()}
          {activeTab === 'zertifikate' && renderZertifikate()}
          {activeTab === 'reklamationen' && renderReklamationen()}
        </>
      )}
    </div>
  );
};

export default QualitaetOverview;
